import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { Check } from "lucide-react";
import { WHY_SHADES } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

function TiltCard({ card, shade, index }) {
  const rm = useReducedMotion();
  const ref = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 160, damping: 22 });
  const ry = useSpring(useMotionValue(0), { stiffness: 160, damping: 22 });

  const onMove = (e) => {
    if (rm || !window.matchMedia("(pointer: fine)").matches || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rx.set(-py * 5);
    ry.set(px * 7);
  };
  const reset = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.85, delay: index * 0.12, ease: EASE }}
      className={`relative overflow-hidden rounded-[2rem] ${shade} border border-cream/5 p-8 sm:p-10 shadow-[0_24px_70px_rgba(0,0,0,0.3)] [perspective:1000px]`}
      data-testid={`why-card-${card.num}`}
    >
      <span
        aria-hidden="true"
        style={{ transform: "translateZ(-40px)" }}
        className="absolute -top-6 -end-2 font-display text-[9rem] font-black leading-none text-cream/[0.045] select-none"
      >
        {card.num}
      </span>
      <div style={{ transform: "translateZ(30px)" }}>
        <span className="font-display text-sm font-bold text-brass">{card.num}</span>
        <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight">{card.title}</h3>
        <p className="mt-4 text-sm sm:text-base leading-relaxed text-cream/70">{card.desc}</p>
        <ul className="mt-7 space-y-3">
          {card.bullets.map((b, i) => (
            <motion.li
              key={b}
              initial={{ opacity: 0, x: -18 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: 0.35 + index * 0.12 + i * 0.1, ease: EASE }}
              className="flex items-start gap-3 text-sm text-cream/85"
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brass/15">
                <Check size={12} strokeWidth={3} className="text-brass" />
              </span>
              {b}
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export default function WhyUs() {
  const { t } = useLang();
  return (
    <div data-testid="why-us-section">
      <motion.h2
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight"
      >
        {t.whyPre} <em className="font-serif italic font-medium text-brasslight">{t.whyAccent}</em>
      </motion.h2>
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {t.whyCards.map((c, i) => (
          <TiltCard key={c.num} card={c} shade={WHY_SHADES[i]} index={i} />
        ))}
      </div>
    </div>
  );
}
